/**
 * Size Mapping Utility
 * 
 * Provides functions for matching sizes across different sizing systems.
 * Uses theme settings configuration output by the `size-mapping-config.liquid` snippet.
 * 
 * This module handles equivalency matching between:
 * - Standard alpha sizes (S, M, L, XL, XXL, etc.)
 * - Tailored dress shirt sizes (14, 14.5, 15, 15.5, etc.)
 * - Tailored jacket/blazer sizes (36, 38, 40, 42, etc.)
 * - Bottom waist sizes (28, 30, 32, 34, etc.)
 * - Belt sizes
 */

class SizeMapping {
  constructor() {
    this.config = null;
    this.flatLookup = new Map(); // Maps normalized size -> array of all equivalent sizes
    this.initialized = false;
  }

  /**
   * Initialize the size mapping from the DOM config element
   * @returns {boolean} Whether initialization was successful
   */
  init() {
    if (this.initialized) return true;

    const configEl = document.getElementById('size-mapping-config');
    if (!configEl) {
      console.warn('[SizeMapping] No size-mapping-config element found. Size matching will use exact match only.');
      this.initialized = true;
      return false;
    }

    try {
      this.config = JSON.parse(configEl.textContent);
      this.buildLookupTable();
      this.initialized = true;
      return true;
    } catch (e) {
      console.error('[SizeMapping] Failed to parse size mapping config:', e);
      this.initialized = true;
      return false;
    }
  }

  /**
   * Build a flat lookup table for fast equivalency checking
   * Maps each normalized size to all its equivalent sizes
   */
  buildLookupTable() {
    if (!this.config) return;

    // Process all categories
    const allCategories = [
      this.config.standardAlpha || [],    // Includes dress shirt neck sizes
      this.config.tailoredJackets || [],
      this.config.bottomWaist || [],
      this.config.belts || []
    ];

    for (const category of allCategories) {
      for (const group of category) {
        if (!Array.isArray(group)) continue;
        
        // Normalize all sizes in the group
        const normalizedGroup = group.map(size => this.normalize(size));
        
        // Map each size to the full group (including itself)
        for (const size of normalizedGroup) {
          if (!this.flatLookup.has(size)) {
            this.flatLookup.set(size, new Set());
          }
          // Add all equivalents
          for (const equiv of normalizedGroup) {
            this.flatLookup.get(size).add(equiv);
          }
        }
      }
    }
  }

  /**
   * Normalize a size string for comparison
   * @param {string} size - The size to normalize
   * @returns {string} Normalized size (lowercase, trimmed, no extra spaces)
   */
  normalize(size) {
    if (size == null) return '';
    return String(size).toLowerCase().trim().replace(/\s+/g, ' ');
  }

  /**
   * Extract numeric part from tailored sizes with fit suffixes
   * e.g., "38R" → "38", "40S" → "40", "42L" → "42"
   * @param {string} size - The size string
   * @returns {string|null} The numeric part or null if not a tailored size
   */
  extractTailoredNumeric(size) {
    if (!size) return null;
    const match = size.match(/^(\d+)[RSL]$/i);
    return match ? match[1] : null;
  }

  /**
   * Check if two sizes are equivalent
   * @param {string} size1 - First size
   * @param {string} size2 - Second size
   * @returns {boolean} Whether the sizes are equivalent
   */
  sizesMatch(size1, size2) {
    this.init();

    const norm1 = this.normalize(size1);
    const norm2 = this.normalize(size2);

    // Exact match after normalization
    if (norm1 === norm2) return true;

    // Check if either size is empty
    if (!norm1 || !norm2) return false;

    // Look up equivalents
    const equivalents = this.flatLookup.get(norm1);
    if (equivalents && equivalents.has(norm2)) {
      return true;
    }

    // Handle tailored jacket sizes with fit suffixes (e.g., "38R", "40S", "42L")
    // The R/S/L suffix indicates Regular/Short/Long fit and should be stripped for equivalence
    const tailored1 = this.extractTailoredNumeric(norm1);
    const tailored2 = this.extractTailoredNumeric(norm2);
    
    // If size1 has tailored suffix, try matching its numeric part
    if (tailored1) {
      if (tailored1 === norm2) return true;
      const equiv1 = this.flatLookup.get(tailored1);
      if (equiv1 && equiv1.has(norm2)) return true;
    }
    
    // If size2 has tailored suffix, try matching its numeric part
    if (tailored2) {
      if (norm1 === tailored2) return true;
      const equiv2 = this.flatLookup.get(norm1);
      if (equiv2 && equiv2.has(tailored2)) return true;
    }
    
    // If both have tailored suffixes, compare numeric parts
    if (tailored1 && tailored2) {
      if (tailored1 === tailored2) return true;
      const equivT1 = this.flatLookup.get(tailored1);
      if (equivT1 && equivT1.has(tailored2)) return true;
    }

    return false;
  }

  /**
   * Get all equivalent sizes for a given size
   * @param {string} size - The size to look up
   * @returns {string[]} Array of equivalent sizes (including the input)
   */
  getEquivalentSizes(size) {
    this.init();

    const normalized = this.normalize(size);
    const equivalents = this.flatLookup.get(normalized);
    
    if (equivalents) {
      return Array.from(equivalents);
    }
    
    // Return the original size if no equivalents found
    return normalized ? [normalized] : [];
  }

  /**
   * Check if a size has any equivalents defined
   * @param {string} size - The size to check
   * @returns {boolean} Whether the size has equivalents in the mapping
   */
  hasMapping(size) {
    this.init();
    return this.flatLookup.has(this.normalize(size));
  }

  /**
   * Debug: Log the current mapping state
   */
  debug() {
    this.init();
    console.group('[SizeMapping] Debug');
    console.log('Config:', this.config);
    console.log('Lookup Table:');
    for (const [key, value] of this.flatLookup) {
      console.log(`  "${key}" =>`, Array.from(value));
    }
    console.groupEnd();
  }

  /**
   * Debug: Test matching between two sizes
   * @param {string} size1 - First size
   * @param {string} size2 - Second size
   */
  testMatch(size1, size2) {
    this.init();
    const matches = this.sizesMatch(size1, size2);
    console.log(`[SizeMapping] "${size1}" matches "${size2}": ${matches}`);
    console.log(`  Equivalents for "${size1}":`, this.getEquivalentSizes(size1));
    console.log(`  Equivalents for "${size2}":`, this.getEquivalentSizes(size2));
    return matches;
  }
}

// Export singleton instance
const sizeMapping = new SizeMapping();

// Make available globally for debugging
if (typeof window !== 'undefined') {
  window.SizeMapping = sizeMapping;
}

export default sizeMapping;
export { SizeMapping };
